import { eachDayOfInterval, endOfDay } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { countBy, identity } from 'ramda';

import { Queue } from '../graphql/components';

/**
 * This utility helps us defining which time zone should be used as default for schedule:
 *  - in case of single-queue schedule, the timezone of this queue should be used
 *  - in case of multi-queue schedule:
 *     - the time zone used by most of the selected queues should be used
 *     - if there are multiple time zones used by same amount of queues,
 *       the time zone of the oldest queue (having lowest ID) should be used
 * @param queues record object of all existing queues
 * @param queueIds array of ids of assigned queues in schedule
 * @returns timezone or undefined when there is no queueId provided
 */
export const chooseTimezoneFromSelectedQueues = (
  queues: Record<string, Queue>,
  queueIds: string[] | string | undefined
) => {
  if (!queueIds?.length) {
    return undefined;
  }

  const usedQueuesTimezones = Array.isArray(queueIds)
    ? queueIds.map((queueId) => queues[queueId]?.timezone)
    : [queues[queueIds]?.timezone];

  if (usedQueuesTimezones.length <= 1) {
    // when usedQueuesTimezones.length === 0, timezone value should be reset to undefined
    return usedQueuesTimezones?.[0];
  }

  const timezonesOccurrences = countBy(identity, usedQueuesTimezones);
  const timezonesSortedByOccurrence = Object.keys(timezonesOccurrences).sort((tz1, tz2) =>
    timezonesOccurrences[tz1] < timezonesOccurrences[tz2] ? 1 : -1
  );

  const countOfMostlyUsedTimezoneOccurrence = timezonesOccurrences[timezonesSortedByOccurrence[0]];

  if (countOfMostlyUsedTimezoneOccurrence > timezonesOccurrences[timezonesSortedByOccurrence[1]]) {
    // there is one timezone used by most of the chosen queues, so this one needs to be used
    return timezonesSortedByOccurrence[0];
  }

  // when multiple time zones are used by the same amount of queues, we need to chose timezone that is being used by the oldest created queue
  const mostlyUsedTimezones = Object.keys(timezonesOccurrences).filter(
    (tz) => timezonesOccurrences[tz] === countOfMostlyUsedTimezoneOccurrence
  );
  const queueIdsWithMostlyUsedTimezones = (queueIds as string[]).filter((queueId) =>
    mostlyUsedTimezones.includes(queues[queueId].timezone)
  );
  const oldestQueueId = queueIdsWithMostlyUsedTimezones.sort((a, b) => (Number(a) < Number(b) ? -1 : 1))[0];

  return queues[oldestQueueId].timezone;
};

export const getPrevalentOffsetDateFromInterval = (start: Date, end: Date, timezone: string) => {
  const days = eachDayOfInterval({ start, end });
  const rangeLength = days.length;

  let firstOffsetCount = 0;
  let lastOffset = getTimezoneOffset(timezone, endOfDay(days[0]));

  for (let i = 0; i < rangeLength / 2; i += 1) {
    const offset = getTimezoneOffset(timezone, endOfDay(days[i]));

    if (offset !== lastOffset) {
      break;
    }

    lastOffset = offset;
    firstOffsetCount += 1;
  }

  return firstOffsetCount <= rangeLength - firstOffsetCount ? end : start;
};
